/**
 * Applicant Form_Renderer — Bound controls
 * (application-form-builder spec, task 23.3).
 *
 * A Bound_Field renders a control but reads/writes a *canonical entity* (its
 * Binding_Target) and stores **no** value in the Application_Response `values`
 * map (Req 17.2). This module supplies the four concrete bound controls the
 * applicant renderer maps each Binding_Target to:
 *
 *   - DIVISION           → a selector over `divisionSelectorOptions(...)` that
 *                          commits the choice to `Participation.divisionKey` via
 *                          `selectDivision` (Req 18.1, 18.2);
 *   - SEASON             → a selector over `selectableSeasons(...)` that commits
 *                          the choice to `Participation.competitionYear` via
 *                          `selectSeason`, mirroring DIVISION
 *                          (director-form-application-cutover Req 4.2, 4.3, 4.7);
 *   - TITLE_PREFERENCES  → a ranked free-text list whose commit returns the
 *                          NON-BLOCKING `Title_Contention_Warning` list, surfaced
 *                          as advisories that never block submit (Req 19.1, 19.3,
 *                          19.4);
 *   - CONTACT_IDENTITY   → locked, read-only identity fields sourced from the
 *                          Person captured at intake (Req 17.3);
 *   - DOCUMENT_SLOT      → a catalog-driven uploader + required checklist over
 *                          `entriesInScope` / `missingRequiredDocuments`
 *                          (Req 20.2).
 *
 * Every rule a bound control depends on (selectable divisions, the locked-
 * contact flag, ranked normalization, in-scope catalog entries, the document
 * gate) is delegated to the SAME I/O-free pure modules the server runs, so a
 * bound control can never disagree with the server about what is selectable,
 * locked, or required. The writes themselves are routed through an injected
 * {@link BoundControlAdapter} seam — exactly the `FileUploadAdapter` precedent —
 * whose method signatures map one-to-one onto the `applicationFormClient`
 * transport (`selectDivision`, `saveTitlePreferences`, document upload), so the
 * container (task 23.4) wires the live client in without this presentational
 * layer importing it. None of these controls write to React Hook Form / the
 * response `values` map (Req 17.2).
 *
 * Validates: Requirements 17.2, 17.3, 18.1, 18.2, 19.1, 19.3, 19.4, 20.2
 */

/*
 * Registry/seam module: it co-locates the per-target control components with the
 * adapter context and the `getBoundControl` resolver. Exporting those non-
 * component bindings alongside the controls is intentional (mirrors
 * `fieldRegistry.tsx`), so the Fast-Refresh "only export components" rule does
 * not apply here.
 */
/* eslint-disable react-refresh/only-export-components */

import * as React from 'react';

import { CheckCircle2, Crown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { compose, stripPrefix } from '@/lib/titlePrefix';
import { RichHelpText } from '@/components/application-form/RichHelpText';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import {
  capRankedPreferences,
  divisionSelectorOptions,
  effectivePhase,
  entriesInScope,
  missingRequiredDocuments,
  phaseLabel,
  validateDocumentUpload,
  type ApplicantType,
  type BindingTarget,
  type Division,
  type DocumentCatalogEntry,
  type FormFieldDef,
  type ResolvedContact,
  type SelectableSeason,
} from './pure';
// The uploaded-document read-model and the remove callback carry the transport's
// `DocumentRef` (it has the `id` the remove path deletes by; apply-document-uploads
// Req 6.1). A type-only import keeps the presentational renderer free of the
// transport at runtime — this is the same client shape the `useApplyDocumentSlots`
// hook (task 5.1) produces, so the adapter the container wires in matches exactly.
import type { DocumentRef } from '@/lib/application-form/client';

// ---------------------------------------------------------------------------
// Bound-write seam
// ---------------------------------------------------------------------------

/**
 * The injected boundary between the bound controls and the canonical writers.
 * Each callback maps one-to-one onto an `applicationFormClient` method so the
 * container (task 23.4) wires the live transport without the renderer importing
 * it; each data field is the resolved canonical source the control displays.
 * Every member is optional so a partially-wired (or read-only preview) renderer
 * still composes — an unwired write control renders disabled, exactly like the
 * `FileUploadAdapter` seam.
 */
export interface BoundControlAdapter {
  // --- Division (Req 18) ---------------------------------------------------
  /** The applicant's currently selected division (`Participation.divisionKey`). */
  currentDivisionKey?: string | null;
  /** Commit a division choice to `Participation.divisionKey` (never to `values`). */
  onSelectDivision?: (divisionKey: string) => Promise<void> | void;

  // --- Season (Req 4.2) ----------------------------------------------------
  /** The applicant's currently selected Season (`Participation.competitionYear`). */
  currentCompetitionYear?: number | null;
  /**
   * The Seasons selectable in a Season_Bound_Field — exactly those whose
   * application open-window includes the current instant, as decided by the
   * SAME pure `selectableSeasons(...)` the `selectSeason` write op validates
   * against (Req 4.3, 4.7). An empty list renders the control's empty state.
   */
  selectableSeasons?: readonly SelectableSeason[];
  /** Commit a Season choice to `Participation.competitionYear` (never to `values`); mirrors `onSelectDivision`. */
  onSelectSeason?: (competitionYear: number) => Promise<void> | void;

  // --- Title preferences (Req 19) ------------------------------------------
  /** The applicant's saved ranked Title_Preferences (response slot, not `values`). */
  titlePreferences?: readonly string[];
  /**
   * The director-decided approved Title (`Participation.approvedTitle`). When the
   * application is APPROVED this is the settled Title, so the Title control drops
   * the ranked-preference editor and presents this single value read-only.
   */
  approvedTitle?: string | null;
  /** Whether the application is APPROVED — flips the Title control to its settled read-only mode. */
  applicationApproved?: boolean;
  /**
   * Persist the ranked Title_Preferences and return the NON-BLOCKING
   * `Title_Contention_Warning` list (the preferences that collide with another
   * applicant's approved title). Mirrors `applicationFormClient.saveTitlePreferences`.
   */
  onSaveTitlePreferences?: (
    ranked: string[],
  ) => Promise<{ contention: string[] }> | { contention: string[] };
  /**
   * The active, in-scope season Title_Catalog options the applicant may choose
   * from (season-scoped-title-registry Req 5.1). When provided, the
   * Title_Preferences control presents these as selectable options instead of a
   * free-text field, and the chosen `label` is persisted (the stored shape is
   * unchanged). This set is resolved STRICTLY for the applicant's current-season
   * `competitionYear`, so a new season's catalog never carries over a prior
   * season's options (Req 5.4). Absent ⇒ the control falls back to free-text
   * entry (flag-off / no catalog).
   */
  availableTitles?: readonly { key: string; label: string }[];
  /**
   * The resolved Division_Title_Prefix for the applicant's current division
   * (title-approval-tracking R3, R4). When set, the free-text Title_Preferences
   * rows display this prefix greyed/non-editable to the LEFT of each input and
   * capture only the Base_Title; the control composes the prefix back onto each
   * base with `compose(...)` before persisting (so the stored ranked list stays
   * the full Composed_Titles) and strips it with `stripPrefix(...)` to seed each
   * input. Absent/blank ⇒ no prefix segment is rendered and the row value is the
   * bare stored title (R4.4).
   */
  titlePrefix?: string;

  // --- Contact / identity (Req 17.3) ---------------------------------------
  /** The Person-sourced contact/identity fields, surfaced locked read-only. */
  contact?: ResolvedContact | null;

  // --- Document slot (Req 20) ----------------------------------------------
  /** The director-configured per-competition Document_Catalog. */
  documentCatalog?: readonly DocumentCatalogEntry[];
  /**
   * The applicant's overall `ApplicationResponse.submissionStatus`, threaded so
   * the document renderer applies the SAME Phase-2 visibility rule as
   * `Phase2Container.tsx`: while this is not `'APPROVED'`, Phase_2 document slots
   * are hidden; once `'APPROVED'` they are revealed. Phase_1 slots are always
   * shown regardless of this value (application-builder-documents Req 5.1, 5.3,
   * 5.4). This client hide is a UX affordance — the presign server gate is
   * authoritative.
   */
  submissionStatus?: string | null;
  /** The applicant's type, for the catalog's applicant-type scope. */
  applicantType?: ApplicantType;
  /** The applicant's uploaded documents, matched to catalog entries by `catalogEntryId`. */
  uploadedDocuments?: readonly DocumentRef[];
  /** Upload picked files for a catalog slot through the unchanged DocumentReference store. */
  onUploadDocument?: (
    entry: DocumentCatalogEntry,
    files: FileList,
  ) => Promise<DocumentRef[] | void> | DocumentRef[] | void;
  /**
   * Remove one uploaded document from its slot through the unchanged
   * DocumentReference store, deleting by `DocumentRef.id`. Mirrors
   * `applicationFormClient.deleteDocumentReference`; absent ⇒ the row's remove
   * control renders disabled (apply-document-uploads Req 6.1, 6.2).
   */
  onRemoveDocument?: (ref: DocumentRef) => Promise<void> | void;
}

/** Renderer-level context carrying the applicant's division/competition scope + the bound-write seam. */
export interface BoundControlContextValue {
  /** The competition the response belongs to (the bound writes' scope). */
  competitionId: string;
  /** The applicant's assigned division key (drives the document catalog scope). */
  divisionKey: string;
  /** The competition's divisions; only `active ∧ appliesToApplications` are selectable. */
  activeDivisions: readonly Division[];
  /** The injected bound-write seam; absent ⇒ controls render read-only/disabled. */
  adapter?: BoundControlAdapter;
}

const BoundControlContext = React.createContext<BoundControlContextValue | null>(null);

/** Provider wiring the bound-write seam + division/competition scope into the bound controls. */
export function BoundControlProvider({
  value,
  children,
}: {
  value: BoundControlContextValue;
  children: React.ReactNode;
}): React.ReactElement {
  return <BoundControlContext.Provider value={value}>{children}</BoundControlContext.Provider>;
}

function useBoundControlContext(): BoundControlContextValue | null {
  return React.useContext(BoundControlContext);
}

// ---------------------------------------------------------------------------
// Shared accessible scaffold (label + help + required marker)
// ---------------------------------------------------------------------------

/** The required marker, hidden from assistive tech (controls also set `aria-required`). */
function RequiredMark(): React.ReactElement {
  return (
    <span className="ml-0.5 text-destructive-text" aria-hidden="true">
      *
    </span>
  );
}

/**
 * The label + help scaffold shared by every bound control. Mirrors the CUSTOM
 * `FieldShell` so bound and custom fields read identically. `htmlFor` is omitted
 * for group/composite controls (which reference `labelId` via `aria-labelledby`).
 */
function BoundFieldShell({
  field,
  labelId,
  helpId,
  htmlFor,
  children,
}: {
  field: FormFieldDef;
  labelId: string;
  helpId: string;
  htmlFor?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="space-y-1.5" data-bound-target={field.bindingTarget}>
      <Label id={labelId} htmlFor={htmlFor} className="text-body font-medium text-foreground">
        <span>
          {field.label}
          {field.required ? <RequiredMark /> : null}
        </span>
      </Label>
      <RichHelpText
        id={helpId}
        format={field.helpTextFormat}
        helpText={field.helpText}
        helpTextHtml={field.helpTextHtml}
        className="text-body-sm text-muted-foreground"
      />
      {children}
    </div>
  );
}

/** The disabled / unwired fallback shown when no bound-write seam is provided. */
function UnwiredBoundNotice({ describedById }: { describedById?: string }): React.ReactElement {
  return (
    <p
      id={describedById}
      role="note"
      className="rounded-input border border-dashed border-border-strong bg-muted/40 px-3 py-2 text-body-sm text-muted-foreground"
    >
      This field is linked to your account information.
    </p>
  );
}

const BOUND_INPUT_CLASS = 'border-border-strong';

// ---------------------------------------------------------------------------
// DIVISION — selector over the active, application-selectable divisions (Req 18)
// ---------------------------------------------------------------------------

function DivisionBoundControl({
  field,
  disabled,
}: {
  field: FormFieldDef;
  disabled?: boolean;
}): React.ReactElement {
  const ctx = useBoundControlContext();
  const controlId = field.id;
  const labelId = `${field.id}-label`;
  const helpId = `${field.id}-help`;
  const statusId = `${field.id}-status`;

  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Options are the SAME `active ∧ appliesToApplications` set, in the same
  // display order, the server validates a selection against (Req 18.1).
  const options = React.useMemo(
    () => divisionSelectorOptions(ctx?.activeDivisions ?? []),
    [ctx?.activeDivisions],
  );

  // The current selection: the canonical `Participation.divisionKey` when known,
  // else the applicant's assigned division key from the render scope.
  const current = ctx?.adapter?.currentDivisionKey ?? ctx?.divisionKey ?? '';
  const canWrite = Boolean(ctx?.adapter?.onSelectDivision) && !disabled;

  const handleChange = async (next: string) => {
    if (!ctx?.adapter?.onSelectDivision || next === '') {
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await ctx.adapter.onSelectDivision(next);
    } catch {
      setError('We could not save your division selection. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BoundFieldShell field={field} labelId={labelId} helpId={helpId} htmlFor={controlId}>
      <Select
        value={current === '' ? undefined : current}
        disabled={!canWrite}
        onValueChange={(next) => void handleChange(next)}
      >
        <SelectTrigger
          id={controlId}
          aria-required={field.required || undefined}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={[field.helpText ? helpId : null, error || saving ? statusId : null]
            .filter(Boolean)
            .join(' ') || undefined}
          className={cn(BOUND_INPUT_CLASS, 'w-full')}
        >
          <SelectValue placeholder="Select a division" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.key} value={option.key}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error ? (
        <p id={statusId} role="alert" className="text-body-sm text-destructive-text">
          {error}
        </p>
      ) : saving ? (
        <p id={statusId} aria-live="polite" className="text-body-sm text-muted-foreground">
          Saving…
        </p>
      ) : null}
    </BoundFieldShell>
  );
}

// ---------------------------------------------------------------------------
// SEASON — selector over the application-open seasons (Req 4.2, 4.3, 4.7)
// ---------------------------------------------------------------------------

function SeasonBoundControl({
  field,
  disabled,
}: {
  field: FormFieldDef;
  disabled?: boolean;
}): React.ReactElement {
  const ctx = useBoundControlContext();
  const controlId = field.id;
  const labelId = `${field.id}-label`;
  const helpId = `${field.id}-help`;
  const statusId = `${field.id}-status`;

  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Options are the SAME intake-open set the `selectSeason` write op validates a
  // selection against (Req 4.3) — resolved by the pure `selectableSeasons(...)`
  // and supplied through the adapter so the control can never offer a Season the
  // server would reject. The select value space is `String(competitionYear)`.
  const options = React.useMemo(
    () => (ctx?.adapter?.selectableSeasons ?? []).slice(),
    [ctx?.adapter?.selectableSeasons],
  );

  // The current selection: the canonical `Participation.competitionYear` when
  // known, surfaced as a string for the Select control.
  const current =
    ctx?.adapter?.currentCompetitionYear != null
      ? String(ctx.adapter.currentCompetitionYear)
      : '';
  const canWrite = Boolean(ctx?.adapter?.onSelectSeason) && !disabled;

  const handleChange = async (next: string) => {
    if (!ctx?.adapter?.onSelectSeason || next === '') {
      return;
    }
    const competitionYear = Number(next);
    if (!Number.isFinite(competitionYear)) {
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await ctx.adapter.onSelectSeason(competitionYear);
    } catch {
      setError('We could not save your season selection. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // The empty-window case: no Season's application open-window includes now, so
  // there is nothing to select (Req 4.7).
  if (options.length === 0) {
    return (
      <BoundFieldShell field={field} labelId={labelId} helpId={helpId}>
        <p className="text-body-sm text-muted-foreground">
          No seasons are currently open for application.
        </p>
      </BoundFieldShell>
    );
  }

  return (
    <BoundFieldShell field={field} labelId={labelId} helpId={helpId} htmlFor={controlId}>
      <Select
        value={current === '' ? undefined : current}
        disabled={!canWrite}
        onValueChange={(next) => void handleChange(next)}
      >
        <SelectTrigger
          id={controlId}
          aria-required={field.required || undefined}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={[field.helpText ? helpId : null, error || saving ? statusId : null]
            .filter(Boolean)
            .join(' ') || undefined}
          className={cn(BOUND_INPUT_CLASS, 'w-full')}
        >
          <SelectValue placeholder="Select a season" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.competitionYear} value={String(option.competitionYear)}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error ? (
        <p id={statusId} role="alert" className="text-body-sm text-destructive-text">
          {error}
        </p>
      ) : saving ? (
        <p id={statusId} aria-live="polite" className="text-body-sm text-muted-foreground">
          Saving…
        </p>
      ) : null}
    </BoundFieldShell>
  );
}

// ---------------------------------------------------------------------------
// TITLE_PREFERENCES — ranked free-text list + non-blocking contention (Req 19)
// ---------------------------------------------------------------------------

/** The Title_Preferences control accepts between zero and three rows (Req 5.1, 5.2). */
const MAX_TITLE_PREFERENCES = 3;
/** Each title-preference row accepts an open-text value of 1–100 characters (Req 5.3). */
const MAX_TITLE_PREFERENCE_LENGTH = 100;

/** A row is over-length when its trimmed value exceeds the 100-character bound (Req 5.3). */
function isRowOverLength(row: string): boolean {
  return row.trim().length > MAX_TITLE_PREFERENCE_LENGTH;
}

function TitlePreferencesBoundControl({
  field,
  disabled,
}: {
  field: FormFieldDef;
  disabled?: boolean;
}): React.ReactElement {
  const ctx = useBoundControlContext();
  const labelId = `${field.id}-label`;
  const helpId = `${field.id}-help`;
  const warningId = `${field.id}-contention`;

  // The resolved Division_Title_Prefix (title-approval-tracking R3, R4). When
  // non-empty it is shown greyed to the LEFT of each free-text row and the row
  // captures only the Base_Title: the seed is `stripPrefix`-ed off the stored
  // Composed_Title and each non-blank base is `compose`-d back before persist,
  // so the stored ranked list stays the full Composed_Titles (storage contract
  // unchanged). Empty ⇒ `compose`/`stripPrefix` are identities and no prefix
  // segment is rendered (R4.4).
  const prefix = ctx?.adapter?.titlePrefix?.trim() ?? '';

  // The editable ranked rows, capped at three (Req 5.1, 5.2). Seeded from the
  // saved preferences (themselves capped at three), each stripped of the resolved
  // prefix so the input shows only the Base_Title; a single trailing blank row is
  // offered so the applicant can add another preference UNTIL three rows exist, at
  // which point "Add another preference" is disabled.
  const [rows, setRows] = React.useState<string[]>(() => {
    const seed = (ctx?.adapter?.titlePreferences ?? [])
      .slice(0, MAX_TITLE_PREFERENCES)
      .map((stored) => stripPrefix(prefix, stored));
    if (seed.length >= MAX_TITLE_PREFERENCES) {
      return seed;
    }
    return seed.length > 0 ? [...seed, ''] : [''];
  });
  const [contention, setContention] = React.useState<string[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const canWrite = Boolean(ctx?.adapter?.onSaveTitlePreferences) && !disabled;
  const atRowCap = rows.length >= MAX_TITLE_PREFERENCES;

  // Catalog-backed selection (season-scoped-title-registry Req 5.1): when the
  // active, in-scope season catalog is supplied, the ranked rows become SELECTS
  // over the director-defined Title labels instead of free text. The stored
  // value is still the label (unchanged persistence shape), so contention and
  // `persist` are unaffected. Resolved strictly for the current-season catalog,
  // so a new season's options never carry over a prior season's (Req 5.4).
  const catalogOptions = ctx?.adapter?.availableTitles ?? null;
  const catalogMode = catalogOptions != null && catalogOptions.length > 0;

  const persist = async (nextRows: string[]) => {
    if (!ctx?.adapter?.onSaveTitlePreferences) {
      return;
    }
    // A row over the 1–100 character bound is rejected at the row (Req 5.3): we
    // do not persist while any row is over-length, so the invalid value is held
    // for the applicant to correct rather than silently dropped or truncated.
    if (nextRows.some(isRowOverLength)) {
      return;
    }
    // Persist the capped (≤3), normalized, deduped ranked list — the exact shape
    // the server stores (Req 5.4) — and surface the returned warnings (advisory
    // only, Req 5.5). A save failure retains the entered values and exposes a
    // retry (Req 5.6); the entered `rows` are never cleared on error.
    // Compose the resolved prefix back onto each entered Base_Title before
    // persisting, so the stored ranked list is the full Composed_Titles
    // (title-approval-tracking R3.1; storage contract unchanged). `compose` with
    // an empty prefix is the identity, and it drops blank bases to `''` which
    // `capRankedPreferences` then omits — so blank rows stay blank/omitted as
    // today. In catalog mode the prefix is unset, so this is a no-op there.
    const ranked = capRankedPreferences(nextRows.map((base) => compose(prefix, base)));
    setSaveError(null);
    setSaving(true);
    try {
      const result = await ctx.adapter.onSaveTitlePreferences(ranked);
      setContention(result.contention);
    } catch {
      setSaveError('We could not save your title preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const updateRow = (index: number, value: string) => {
    setRows((prev) => {
      const next = prev.slice();
      next[index] = value;
      return next;
    });
  };

  // A catalog selection commits immediately (a Select has no blur-to-save step),
  // updating the row and persisting the new ranked list in one step.
  const selectRow = (index: number, value: string) => {
    const next = rows.slice();
    next[index] = value;
    setRows(next);
    void persist(next);
  };

  // Append a blank row only while under the three-row cap (Req 5.2): once three
  // rows exist the fourth is prevented and the existing rows are preserved.
  const addRow = () =>
    setRows((prev) => (prev.length >= MAX_TITLE_PREFERENCES ? prev : [...prev, '']));

  const removeRow = (index: number) => {
    setRows((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [''];
    });
    void persist(rows.filter((_, i) => i !== index));
  };

  // Contention is matched case-insensitively against the warning list returned
  // by the last save, so the per-row badge cannot drift from the saved result.
  const contendedSet = React.useMemo(
    () => new Set(contention.map((c) => c.toLowerCase())),
    [contention],
  );

  // Settled-Title mode: once the application is APPROVED with a director-decided
  // approved title, the ranked-preference editor is retired and the single
  // approved Title is presented read-only under a plain "Title" label. (The
  // enclosing card heading is likewise relabeled "Title" by FormRenderer.)
  const settledTitle =
    ctx?.adapter?.applicationApproved && ctx.adapter.approvedTitle?.trim()
      ? ctx.adapter.approvedTitle.trim()
      : null;
  if (settledTitle) {
    return (
      <div
        data-bound-target={field.bindingTarget}
        className="flex items-center gap-3 rounded-card border border-primary/20 bg-primary/5 px-4 py-3"
      >
        <Crown className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
        <p id={labelId} className="font-display text-xl leading-tight text-foreground">
          {settledTitle}
        </p>
      </div>
    );
  }

  return (
    <BoundFieldShell field={field} labelId={labelId} helpId={helpId}>
      <div role="group" aria-labelledby={labelId} className="space-y-2">
        {rows.map((row, index) => {
          const rowId = `${field.id}-pref-${index}`;
          // Contention still matches on the stored/composed value, so composing
          // the entered Base_Title with the resolved prefix keeps the per-row
          // badge aligned with the server's Composed_Title warnings (behavior
          // unchanged; title-approval-tracking R3, R4).
          const composedRow = catalogMode ? row.trim() : compose(prefix, row);
          const contended = composedRow !== '' && contendedSet.has(composedRow.toLowerCase());
          const overLength = isRowOverLength(row);
          const rowDescribedBy =
            [contended ? `${rowId}-warning` : null, overLength ? `${rowId}-error` : null]
              .filter(Boolean)
              .join(' ') || undefined;
          return (
            <div key={rowId} className="space-y-1">
              <div className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className="w-5 shrink-0 text-body-sm text-muted-foreground tabular-nums"
                >
                  {index + 1}.
                </span>
                {catalogMode ? (
                  (() => {
                    // Options are the active, in-scope catalog labels, minus the
                    // labels already chosen in the OTHER rows (so a title can't be
                    // ranked twice); the current row's own value is always kept
                    // selectable. A seeded value not in the catalog (e.g. a legacy
                    // free-text preference) is surfaced as its own option so it
                    // still displays rather than silently vanishing.
                    const chosenElsewhere = new Set(
                      rows
                        .filter((_, i) => i !== index)
                        .map((r) => r.trim())
                        .filter((r) => r !== ''),
                    );
                    const options = (catalogOptions ?? []).filter(
                      (opt) => !chosenElsewhere.has(opt.label),
                    );
                    const rowValue = row.trim();
                    const hasRowValue =
                      rowValue !== '' && options.some((opt) => opt.label === rowValue);
                    return (
                      <Select
                        value={rowValue === '' ? undefined : rowValue}
                        disabled={!canWrite}
                        onValueChange={(next) => selectRow(index, next)}
                      >
                        <SelectTrigger
                          id={rowId}
                          aria-label={`Title preference ${index + 1}`}
                          aria-describedby={rowDescribedBy}
                          className={cn(BOUND_INPUT_CLASS, 'flex-1')}
                        >
                          <SelectValue placeholder="Select a title" />
                        </SelectTrigger>
                        <SelectContent>
                          {!hasRowValue && rowValue !== '' ? (
                            <SelectItem value={rowValue}>{rowValue}</SelectItem>
                          ) : null}
                          {options.map((opt) => (
                            <SelectItem key={opt.key} value={opt.label}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    );
                  })()
                ) : (
                  <>
                    {prefix !== '' ? (
                      // The resolved Division_Title_Prefix, shown greyed and
                      // non-editable to the LEFT of the free-text Base_Title input
                      // (e.g. `Miss | Boise`). The stored value stays the full
                      // Composed_Title (title-approval-tracking R3, R4).
                      <span
                        data-testid={`title-prefix-${index}`}
                        aria-hidden="true"
                        className="shrink-0 select-none text-body-sm text-muted-foreground"
                      >
                        {prefix}
                      </span>
                    ) : null}
                    <Input
                      id={rowId}
                      value={row}
                      disabled={!canWrite}
                      maxLength={MAX_TITLE_PREFERENCE_LENGTH}
                      aria-label={`Title preference ${index + 1}`}
                      aria-invalid={overLength || undefined}
                      aria-describedby={rowDescribedBy}
                      className={cn(BOUND_INPUT_CLASS, 'flex-1')}
                      onChange={(e) => updateRow(index, e.target.value)}
                      onBlur={() => void persist(rows)}
                    />
                  </>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={!canWrite || (rows.length === 1 && row.trim() === '')}
                  onClick={() => removeRow(index)}
                >
                  Remove
                  <span className="sr-only"> title preference {index + 1}</span>
                </Button>
              </div>
              {overLength ? (
                <p id={`${rowId}-error`} role="alert" className="pl-7 text-body-sm text-destructive-text">
                  Keep each title preference to {MAX_TITLE_PREFERENCE_LENGTH} characters or fewer.
                </p>
              ) : null}
              {contended ? (
                <p
                  id={`${rowId}-warning`}
                  className="pl-7 text-body-sm text-warning-text"
                >
                  Heads up: “{composedRow}” is already an approved title for another applicant. You
                  can still request it.
                </p>
              ) : null}
            </div>
          );
        })}

        <div className="flex items-center gap-3 pl-7">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canWrite || atRowCap}
            onClick={addRow}
          >
            Add another preference
          </Button>
          {saving ? (
            <span aria-live="polite" className="text-body-sm text-muted-foreground">
              Saving…
            </span>
          ) : null}
        </div>

        {saveError ? (
          <div className="flex items-center gap-3 pl-7">
            <p role="alert" className="text-body-sm text-destructive-text">
              {saveError}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canWrite}
              onClick={() => void persist(rows)}
            >
              Retry
            </Button>
          </div>
        ) : null}

        {/* A non-focus-stealing summary so the advisory nature is announced once. */}
        <p id={warningId} className="sr-only" aria-live="polite">
          {contention.length === 0
            ? 'No title contention warnings.'
            : `${contention.length} of your title preferences are already approved for other applicants. These are advisory only and do not block submission.`}
        </p>
      </div>
    </BoundFieldShell>
  );
}

// ---------------------------------------------------------------------------
// CONTACT_IDENTITY — locked, read-only identity from the Person at intake (Req 17.3)
// ---------------------------------------------------------------------------

/** The locked contact/identity lines, in a stable presentation order. */
function contactLines(contact: ResolvedContact): { label: string; value: string }[] {
  const lines: { label: string; value: string }[] = [
    { label: 'Name', value: `${contact.legalFirstName} ${contact.legalLastName}`.trim() },
    { label: 'Email', value: contact.primaryEmail },
  ];
  if (contact.mobilePhone) {
    lines.push({ label: 'Phone', value: contact.mobilePhone });
  }
  if (contact.dateOfBirth) {
    lines.push({ label: 'Birthday', value: contact.dateOfBirth });
  }
  if (contact.address) {
    const { street, city, state, zip } = contact.address;
    lines.push({ label: 'Address', value: `${street}, ${city}, ${state} ${zip}`.trim() });
  }
  return lines.filter((line) => line.value.trim() !== '');
}

function ContactIdentityBoundControl({
  field,
}: {
  field: FormFieldDef;
}): React.ReactElement {
  const ctx = useBoundControlContext();
  const labelId = `${field.id}-label`;
  const helpId = `${field.id}-help`;
  const noticeId = `${field.id}-locked-note`;
  const contact = ctx?.adapter?.contact ?? null;
  const lines = contact ? contactLines(contact) : [];

  return (
    <BoundFieldShell field={field} labelId={labelId} helpId={helpId}>
      <div
        aria-labelledby={labelId}
        aria-describedby={noticeId}
        className="space-y-2 rounded-input border border-border-strong bg-muted/40 px-3 py-3"
      >
        {lines.length > 0 ? (
          <dl className="space-y-1.5">
            {lines.map((line) => (
              <div key={line.label} className="flex flex-wrap gap-x-2 text-body-sm">
                <dt className="font-medium text-muted-foreground">{line.label}:</dt>
                <dd className="text-foreground">{line.value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-body-sm text-muted-foreground">
            Your account information will appear here.
          </p>
        )}
        <p id={noticeId} className="flex items-center gap-1.5 text-body-sm text-muted-foreground">
          <span aria-hidden="true">🔒</span>
          Locked — managed from your account profile, not this application.
        </p>
      </div>
    </BoundFieldShell>
  );
}

// ---------------------------------------------------------------------------
// DOCUMENT_SLOT — catalog-driven uploader + required checklist (Req 20.2)
// ---------------------------------------------------------------------------

/** Whether a catalog entry's Division scope includes `divisionKey` (null/empty = all divisions). */
function entryVisibleForDivision(entry: DocumentCatalogEntry, divisionKey: string): boolean {
  const keys = entry.visibleDivisionKeys;
  return keys == null || keys.length === 0 || keys.includes(divisionKey);
}

/** Whether a catalog entry applies to ALL applicant types (null/'BOTH' scope). */
function entryAppliesToAllApplicantTypes(entry: DocumentCatalogEntry): boolean {
  return entry.applicantTypeScope == null || entry.applicantTypeScope === 'BOTH';
}

function DocumentSlotBoundControl({
  field,
}: {
  field: FormFieldDef;
}): React.ReactElement {
  const ctx = useBoundControlContext();
  const labelId = `${field.id}-label`;
  const helpId = `${field.id}-help`;

  const catalog = React.useMemo(
    () => (ctx?.adapter?.documentCatalog ?? []).slice(),
    [ctx?.adapter?.documentCatalog],
  );
  const uploaded = React.useMemo(
    () => (ctx?.adapter?.uploadedDocuments ?? []).slice(),
    [ctx?.adapter?.uploadedDocuments],
  );
  const applicantType = ctx?.adapter?.applicantType;
  const divisionKey = ctx?.divisionKey ?? '';
  const submissionStatus = ctx?.adapter?.submissionStatus;

  // In-scope slots + the required-document gate are computed by the SAME pure
  // rules the server's submission gate uses, so the checklist the applicant sees
  // matches exactly what blocks submit (Req 20.2, 20.3).
  const inScope = React.useMemo(() => {
    if (applicantType === undefined) {
      // Applicant type not resolved yet: show the slots that apply to ALL
      // applicant types (null/'BOTH' scope) and are in the applicant's Division
      // scope — the ones the server enforces regardless of type. Type-specific
      // (MISS/MRS) slots are deferred until the type is known, so the checklist
      // can never disagree with the gate.
      return catalog.filter(
        (entry) =>
          entryAppliesToAllApplicantTypes(entry) &&
          entryVisibleForDivision(entry, divisionKey),
      );
    }
    return entriesInScope(catalog, divisionKey, applicantType);
  }, [catalog, divisionKey, applicantType]);

  const missing = React.useMemo(() => {
    const uploadedForGate = uploaded.map((doc) => ({ catalogEntryId: doc.catalogEntryId ?? '' }));
    if (applicantType === undefined) {
      // Match the gate: an in-scope (all-types, in-division) required slot with
      // no matching upload is missing.
      const uploadedEntryIds = new Set(uploadedForGate.map((u) => u.catalogEntryId));
      return inScope.filter(
        (entry) => entry.requiredForSubmission && !uploadedEntryIds.has(entry.id),
      );
    }
    return missingRequiredDocuments(catalog, divisionKey, applicantType, uploadedForGate);
  }, [catalog, divisionKey, applicantType, uploaded, inScope]);

  const missingIds = React.useMemo(() => new Set(missing.map((e) => e.id)), [missing]);

  // The slots actually presented to the applicant: the in-scope slice with the
  // Phase-2 hide applied and sorted into ascending Display_Order.
  //
  //   - Phase-2 hide (Req 5.1, 5.3, 5.4): while the applicant's overall
  //     `submissionStatus` is not `'APPROVED'`, drop the Phase_2 slots; once
  //     `'APPROVED'` they are revealed. Phase_1 slots are ALWAYS shown. This
  //     mirrors `Phase2Container.tsx` and is a UX affordance only — the presign
  //     server gate is authoritative. The derivation reuses the SAME pure
  //     `effectivePhase` the server runs, so the client can never disagree about
  //     a slot's phase.
  //   - Ascending Display_Order (Req 4.5): entries carrying a numeric
  //     `displayOrder` sort ascending; legacy entries without one keep their
  //     (server-supplied) relative order via a stable sort, so a never-reordered
  //     catalog still presents in a total order.
  const visible = React.useMemo(() => {
    const approved = submissionStatus === 'APPROVED';
    const shown = inScope.filter(
      (entry) => approved || effectivePhase(entry) === 'PHASE_1',
    );
    return shown
      .map((entry, index) => ({ entry, index }))
      .sort((a, b) => {
        const ao =
          typeof a.entry.displayOrder === 'number'
            ? a.entry.displayOrder
            : Number.POSITIVE_INFINITY;
        const bo =
          typeof b.entry.displayOrder === 'number'
            ? b.entry.displayOrder
            : Number.POSITIVE_INFINITY;
        return ao !== bo ? ao - bo : a.index - b.index;
      })
      .map(({ entry }) => entry);
  }, [inScope, submissionStatus]);

  const uploadsByEntry = React.useMemo(() => {
    const map = new Map<string, DocumentRef[]>();
    for (const doc of uploaded) {
      const key = doc.catalogEntryId ?? '';
      const list = map.get(key) ?? [];
      list.push(doc);
      map.set(key, list);
    }
    return map;
  }, [uploaded]);

  return (
    <BoundFieldShell field={field} labelId={labelId} helpId={helpId}>
      {visible.length === 0 ? (
        <p className="text-body-sm text-muted-foreground">
          No documents are required for your division.
        </p>
      ) : (
        <ul aria-labelledby={labelId} className="space-y-2">
          {visible.map((entry) => (
            <DocumentSlotRow
              key={entry.id}
              entry={entry}
              uploads={uploadsByEntry.get(entry.id) ?? []}
              satisfiedRequired={entry.requiredForSubmission && !missingIds.has(entry.id)}
              // Document upload/remove is intentionally ALWAYS available: it is
              // never gated by the form-level approval/Phase-1 lock or by a
              // slot's required/approved state, so a contestant can add or
              // replace documents at any time (including after approval). Only
              // adapter wiring + in-flight (busy/removing) state gate it.
              disabled={false}
            />
          ))}
        </ul>
      )}
    </BoundFieldShell>
  );
}

/** One catalog slot: its required indicator, uploader, uploaded-file list, and per-slot errors. */
function DocumentSlotRow({
  entry,
  uploads,
  satisfiedRequired,
  disabled,
}: {
  entry: DocumentCatalogEntry;
  uploads: DocumentRef[];
  satisfiedRequired: boolean;
  disabled?: boolean;
}): React.ReactElement {
  const ctx = useBoundControlContext();
  const inputId = `doc-${entry.id}`;
  const errorId = `${inputId}-error`;
  const statusId = `${inputId}-status`;
  const helpId = `${inputId}-help`;

  // The applicant-facing Help_Text region (Req 2.5, 2.6): rendered iff the entry
  // carries a non-empty Help_Text, and wired to the uploader via
  // `aria-describedby`. An absent/blank value ⇒ no region at all, so the slot is
  // presented without a help-text region. The upsert op already normalizes empty/
  // whitespace to absent; the trim guard here keeps the render robust for legacy
  // rows.
  const helpText =
    typeof entry.helpText === 'string' && entry.helpText.trim() !== '' ? entry.helpText : null;

  // The entry's phase label — exactly "Phase 1" / "Phase 2", derived by the SAME
  // pure `effectivePhase` the server runs, and NEVER "Required"/"Supplemental"
  // (Req 3.4). A satisfied required slot additionally shows a "Provided" marker.
  const phase = phaseLabel(effectivePhase(entry));
  const provided = entry.requiredForSubmission && satisfiedRequired;

  const [busy, setBusy] = React.useState(false);
  const [removingId, setRemovingId] = React.useState<string | null>(null);
  // A single per-slot error region (role="alert"): a pre-upload validation
  // rejection (Req 5.1–5.4), a three-stage upload failure (Req 9.1–9.3), or a
  // remove failure (Req 6). `retryable` marks the upload failures whose last
  // selected files can be re-sent from a Retry control.
  const [error, setError] = React.useState<{ message: string; retryable: boolean } | null>(null);
  // The last selected files, retained so an upload failure can be retried with
  // the SAME files without the applicant re-picking them (Req 9.1, 9.3).
  const pendingFilesRef = React.useRef<FileList | null>(null);

  const canUpload = Boolean(ctx?.adapter?.onUploadDocument) && !disabled;
  const canRemove = Boolean(ctx?.adapter?.onRemoveDocument) && !disabled;

  const runUpload = async (files: FileList) => {
    if (!ctx?.adapter?.onUploadDocument) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await ctx.adapter.onUploadDocument(entry, files);
      // Success — the adapter refreshes `uploadedDocuments`; drop the retry
      // buffer so a later, unrelated failure never re-sends stale files.
      pendingFilesRef.current = null;
    } catch (err) {
      // Three-stage failure handling (presign → S3 PUT → createDocumentReference):
      // surface a per-slot, retryable message; the slot's previously uploaded
      // references are retained by the adapter (Req 9.4).
      setError({
        message:
          err instanceof Error && err.message
            ? err.message
            : 'We couldn’t upload your file. Please try again.',
        retryable: true,
      });
    } finally {
      setBusy(false);
    }
  };

  const handlePick = async (files: FileList | null) => {
    if (!files || files.length === 0 || !ctx?.adapter?.onUploadDocument) {
      return;
    }
    // Pre-upload validation against the catalog's declared constraints, using
    // the SAME pure rule the server enforces — reject BEFORE any network call
    // (Req 5.1–5.4) and RETAIN the slot's existing uploads so the applicant can
    // simply pick a different file (Req 5.5, 6.4). The count grows across a
    // multi-file selection so `maxFileCount` is enforced for the batch (Req 5.3).
    const selected = Array.from(files);
    for (let i = 0; i < selected.length; i += 1) {
      const file = selected[i];
      const violation = validateDocumentUpload(
        entry,
        { contentType: file.type, sizeBytes: file.size },
        uploads.length + i,
      );
      if (violation) {
        pendingFilesRef.current = null;
        setError({ message: violation.message, retryable: false });
        return;
      }
    }

    pendingFilesRef.current = files;
    await runUpload(files);
  };

  const handleRetry = async () => {
    const files = pendingFilesRef.current;
    if (!files) {
      return;
    }
    await runUpload(files);
  };

  const handleRemove = async (doc: DocumentRef) => {
    if (!ctx?.adapter?.onRemoveDocument) {
      return;
    }
    setRemovingId(doc.id);
    setError(null);
    try {
      await ctx.adapter.onRemoveDocument(doc);
    } catch (err) {
      setError({
        message:
          err instanceof Error && err.message
            ? err.message
            : 'We couldn’t remove that document. Please try again.',
        retryable: false,
      });
    } finally {
      setRemovingId(null);
    }
  };

  const hasUploads = uploads.length > 0;

  const describedBy =
    [
      helpText ? helpId : null,
      error ? errorId : null,
      busy ? statusId : null,
      hasUploads ? `${inputId}-files` : null,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <li
      className={cn(
        'rounded-input border p-3 transition-colors',
        // A slot with an attached file reads as "done" at a glance: a soft
        // success tint + accent border, no spelled-out status needed.
        hasUploads ? 'border-success/40 bg-success-muted/30' : 'border-border-strong',
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label htmlFor={inputId} className="text-body font-medium text-foreground">
          {entry.label}
        </Label>
        {/* The entry's phase label + a friendly "provided" cue: a success check
            icon (visual) with sr-only text so assistive tech still reads the
            status (Req 3.4, 11.2). Never "Required"/"Supplemental". */}
        <span className="flex items-center gap-2 text-body-sm font-medium">
          <span className="text-muted-foreground">{phase}</span>
          {provided ? (
            <CheckCircle2 className="h-4 w-4 text-success-text" aria-hidden="true" />
          ) : null}
          {provided ? <span className="sr-only">Provided</span> : null}
        </span>
      </div>
      {/* The applicant-facing Help_Text, shown beneath the label and above the
          uploader (Req 2.5). Absent ⇒ no region (Req 2.6). */}
      {helpText ? (
        <p id={helpId} className="mt-1 text-body-sm text-muted-foreground">
          {helpText}
        </p>
      ) : null}
      <Input
        id={inputId}
        type="file"
        disabled={!canUpload || busy}
        aria-required={entry.requiredForSubmission || undefined}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={describedBy}
        className={cn(BOUND_INPUT_CLASS, 'mt-2 cursor-pointer file:mr-3 file:cursor-pointer')}
        onChange={(e) => void handlePick(e.target.files)}
      />
      {/* The in-progress state, announced politely (Req 2.7, 11.2). */}
      {busy ? (
        <p id={statusId} aria-live="polite" className="mt-1 text-body-sm text-muted-foreground">
          Uploading…
        </p>
      ) : null}
      {/* The per-slot failure region, exposed as an assistive-technology alert (Req 9, 11.3). */}
      {error ? (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <p id={errorId} role="alert" className="text-body-sm text-destructive-text">
            {error.message}
          </p>
          {error.retryable ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canUpload || busy}
              onClick={() => void handleRetry()}
            >
              Retry upload
            </Button>
          ) : null}
        </div>
      ) : null}
      {hasUploads ? (
        <ul id={`${inputId}-files`} className="mt-2 space-y-1.5 text-body-sm">
          {uploads.map((doc) => {
            const removing = removingId === doc.id;
            return (
              <li
                key={doc.id}
                // An attached file is rendered as a success chip — a check icon
                // + the filename on a soft success surface — so it clearly reads
                // as uploaded without a spelled-out label.
                className="flex items-center gap-2 rounded-input border border-success/30 bg-success-muted/50 px-2.5 py-1.5"
              >
                <CheckCircle2
                  className="h-4 w-4 shrink-0 text-success-text"
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                  {doc.fileName}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={!canRemove || removing || busy}
                  onClick={() => void handleRemove(doc)}
                >
                  {removing ? 'Removing…' : 'Remove'}
                  <span className="sr-only"> {doc.fileName}</span>
                </Button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Registry + dispatcher
// ---------------------------------------------------------------------------

/**
 * Resolve the bound control component for a Binding_Target. Returns `null` for
 * an unknown/absent target so the caller can render a safe fallback rather than
 * throw (definition-time validation already constrains the target set).
 */
export function getBoundControl(
  target: BindingTarget | undefined,
): React.ComponentType<{ field: FormFieldDef; disabled?: boolean }> | null {
  switch (target) {
    case 'DIVISION':
      return DivisionBoundControl;
    case 'SEASON':
      return SeasonBoundControl;
    case 'TITLE_PREFERENCES':
      return TitlePreferencesBoundControl;
    case 'CONTACT_IDENTITY':
      return ContactIdentityBoundControl;
    case 'DOCUMENT_SLOT':
      return DocumentSlotBoundControl;
    default:
      return null;
  }
}

/**
 * The single entry point the `FieldRenderer` uses for a BOUND field: dispatch to
 * the control for its Binding_Target. When the target is unknown or the bound-
 * write seam is absent, render the accessible locked/linked notice so the page
 * composition (ordering, visibility) stays correct even before wiring.
 */
export function BoundFieldControl({
  field,
  disabled,
}: {
  field: FormFieldDef;
  disabled?: boolean;
}): React.ReactElement {
  const ctx = useBoundControlContext();
  const Control = getBoundControl(field.bindingTarget);

  if (Control && ctx) {
    return <Control field={field} disabled={disabled} />;
  }

  // No seam (or unknown target): render the label scaffold + linked notice.
  const labelId = `${field.id}-label`;
  const helpId = `${field.id}-help`;
  const noticeId = `${field.id}-notice`;
  return (
    <BoundFieldShell field={field} labelId={labelId} helpId={helpId}>
      <UnwiredBoundNotice describedById={noticeId} />
    </BoundFieldShell>
  );
}
